import gsap from 'gsap'
import ScrollTrigger from 'gsap/ScrollTrigger'

import { createLineReveal } from './lineReveal'

gsap.registerPlugin(ScrollTrigger)

/**
 * Reveal one or more headings line by line the first time each scrolls into
 * view. Each target gets its own {@link createLineReveal} controller (so it
 * starts hidden behind its clip masks) and a one-shot ScrollTrigger that plays
 * the reveal on enter.
 *
 * The split changes line wrapping once web fonts load, which can shift element
 * heights — so ScrollTrigger is refreshed after fonts are ready to keep the
 * trigger positions accurate.
 *
 * @param {string|Element|Element[]|NodeList} targets - elements (or a selector) to reveal
 * @param {object} [options] - { start, ...lineRevealOptions }
 * @param {string} [options.start='top 80%'] - ScrollTrigger start position
 * @returns {Array<{ reveal: ReturnType<typeof createLineReveal>, trigger: ScrollTrigger }>}
 */
export function createScrollReveal(targets, options = {}) {
  const { start = 'top 80%', ...revealOptions } = options
  const elements = gsap.utils.toArray(targets)

  const controllers = elements.map((el) => {
    const reveal = createLineReveal(el, revealOptions)
    const trigger = ScrollTrigger.create({
      trigger: el,
      start,
      once: true,
      onEnter: () => reveal.play(),
    })
    return { reveal, trigger }
  })

  if (controllers.length && document.fonts) {
    document.fonts.ready.then(() => ScrollTrigger.refresh())
  }

  return controllers
}
